import toast from "solid-toast";
import { buildAnalysisTranscript, buildAnalysisTranscriptTagged, buildTranslatedTranscript } from "./transcriptBuilders";
import type { TranscriptUtterance } from "./transcriptBuilders";
import type { DetectedQuestion } from "../../types/detectQuestions";
import { processIntakeFile, processIntakeText } from "../../api/intakeProcessing";
import { updateSession, triggerIntakeRanking, getSession } from "../../api/sessions";
type SessionWorkflowStatus = "draft" | "in_progress" | "completed";

type ExtractAnswerResponse = {
  answer?: string;
  paraphrase?: string;
  quote?: string;
  confidence?: number | null;
};

const normalizeDigits = (s: string) => (s || "").replace(/\D/g, "");

/**
 * Finalization pipeline extracted from the interview session page:
 * - stop recording
 * - reconcile unanswered questions (AI extraction + grounding checks)
 * - optional post-hoc suggestions backfill
 * - generate insights
 * - generate report + mark session completed
 *
 * This is intentionally "orchestrator-y", but isolated from UI composition.
 */
export function useSessionFinalization(input: {
  sessionId: () => string | null;
  sourceLang: () => string;
  likelihood: () => number | undefined;
  setLikelihood: (v: number | undefined) => void;
  setScreeningDecision: (v: string | undefined) => void;
  setMaltreatment: (v: string | undefined) => void;
  setPriorityRanking: (v: number | undefined) => void;
  setLlmPredictionExplanation: (v: string | undefined) => void;
  setAlerts: (v: string[] | undefined) => void;
  setOffenderRole: (v: string | undefined) => void;
  setVictimType: (v: string | undefined) => void;
  contactType: () => string;
  interpreterEnabled: () => boolean;
  utterances: () => TranscriptUtterance[];
  caseState: () => any;
  setCaseState: (fn: (prev: any) => any) => void;
  persistInterviewMetadata?: (extra?: any) => void;
  stopAll: () => Promise<void>;
  setWorkflowStatus: (v: SessionWorkflowStatus) => void;
  setReadOnly: (v: boolean) => void;
  setPopupMsg: (v: string | null) => void;
  buildEnglishTranscript: (uts: TranscriptUtterance[]) => string;
  buildEnglishWindowFromAskedAt: (askedAt: any, hardCapMs?: number) => string;
  refineAiAskedAt: (
    questionText: string,
    approxAskedAt: any,
  ) => string | undefined;
  extractAnswer: (input: {
    question: string;
    transcript: string;
    contactType: string;
    strict: boolean;
  }) => Promise<ExtractAnswerResponse>;
  detectQuestions: (input: {
    recentTranscript: string;
    suggestedQuestions?: any[];
    alreadyAsked: string[];
    sessionId?: string | null;
    contactType?: string | null;
    mode?: "adhoc";
  }) => Promise<{ detectedQuestions: DetectedQuestion[] }>;
  fetchInsights: (
    transcript: string,
    contactType: string,
    sessionId?: string,
  ) => Promise<void>;
  generateReport: (
    sessionId: string,
    opts: { contactType: string; forceNewVersion: boolean },
  ) => Promise<any>;
  updateSession: (sessionId: string, updates: any) => Promise<any>;
}) {
  const runWithPool = async <T>(
    items: readonly T[],
    worker: (item: T) => Promise<void>,
    pool = 3,
  ) => {
    const queue = items.slice() as T[];
    const runners: Promise<void>[] = [];
    for (let i = 0; i < Math.min(pool, queue.length); i++) {
      runners.push(
        (async function loop() {
          while (queue.length) {
            const next = queue.shift()!;
            try {
              await worker(next);
            } catch {
              // intentionally swallow per-item failures; pipeline continues
            }
          }
        })(),
      );
    }
    await Promise.allSettled(runners);
  };

  const tryExtract = async (
    question: string,
    transcript: string,
  ): Promise<{
    answer: string;
    paraphrase: string;
    quote: string;
    confidence: number | undefined;
  }> => {
    try {
      const dj = await input.extractAnswer({
        question,
        transcript,
        contactType: input.contactType(),
        strict: true,
      });
      return {
        answer: dj?.answer || "",
        paraphrase: dj?.paraphrase || "",
        quote: dj?.quote || "",
        confidence:
          typeof dj?.confidence === "number" ? dj.confidence : undefined,
      };
    } catch {
      return { answer: "", paraphrase: "", quote: "", confidence: undefined };
    }
  };

  /** Check whether the LLM returned a usable answer (not blank / not "No clear answer"). */
  const hasAnswer = (answer: string) => {
    const ans = String(answer || "").trim();
    return !!ans && ans !== "No clear answer provided";
  };

  /** Derive answerStatus from LLM confidence: >= 0.5 → "answered", else "inferred" (Needs Review). */
  const statusFromConfidence = (
    confidence: number | undefined,
  ): "answered" | "inferred" =>
    typeof confidence === "number" && confidence >= 0.5
      ? "answered"
      : "inferred";

  async function reconcileUnansweredQuestions(canonicalTranscript: string) {
    const unanswered = (input.caseState().answeredQuestions || []).filter(
      (qa: any) => !qa.answer || !String(qa.answer).trim(),
    );

    const unansweredWorker = async (qa: any) => {
      const src = (qa && (qa as any).source) || undefined;
      const askedAtIso = (qa && (qa as any).askedAt) || undefined;
      const anchoredAskedAt =
        src === "ai"
          ? input.refineAiAskedAt(qa.question, askedAtIso)
          : askedAtIso;

      const windowText = input.buildEnglishWindowFromAskedAt(
        anchoredAskedAt,
        45_000,
      );

      let extracted = "";
      let extractedParaphrase = "";
      let extractedQuote = "";
      let extractedConfidence: number | undefined = undefined;

      if (windowText && windowText.trim().length > 0) {
        const res = await tryExtract(qa.question, windowText);
        extracted = res.answer;
        extractedParaphrase = res.paraphrase || "";
        extractedQuote = res.quote;
        extractedConfidence = res.confidence;
      }

      if (
        (!extracted || extracted === "No clear answer provided") &&
        canonicalTranscript &&
        canonicalTranscript.trim().length > 0
      ) {
        const res = await tryExtract(
          qa.question,
          buildAnalysisTranscriptTagged(
            input.utterances(),
            input.interpreterEnabled(),
          ),
        );
        extracted = res.answer;
        extractedParaphrase = res.paraphrase || "";
        extractedQuote = res.quote;
        extractedConfidence = res.confidence;
      }

      if (hasAnswer(extracted)) {
        // LLM found an answer — accept it, tag based on confidence
        input.setCaseState((prev) => ({
          ...prev,
          answeredQuestions: prev.answeredQuestions.map((x: any) =>
            x.question === qa.question
              ? {
                  ...x,
                  answer: extracted,
                  paraphrase: extractedParaphrase || x.paraphrase || undefined,
                  quote: extractedQuote || x.quote,
                  confidence:
                    extractedConfidence !== undefined
                      ? extractedConfidence
                      : x.confidence,
                  answeredAt: new Date().toISOString(),
                  answerStatus: statusFromConfidence(extractedConfidence),
                }
              : x,
          ),
        }));
        input.persistInterviewMetadata?.();
      } else {
        // LLM genuinely couldn't find an answer
        input.setCaseState((prev) => ({
          ...prev,
          answeredQuestions: prev.answeredQuestions.map((x: any) =>
            x.question === qa.question
              ? {
                  ...x,
                  answerStatus: x.answerStatus || "no_answer",
                }
              : x,
          ),
        }));
        input.persistInterviewMetadata?.();
      }
    };

    await runWithPool(unanswered, unansweredWorker, 5);
  }

  async function detectAdhocQuestions(canonicalTranscript: string) {
    try {
      const tagged = buildAnalysisTranscriptTagged(
        input.utterances(),
        input.interpreterEnabled(),
      );
      if (!tagged.trim()) return;

      const alreadyAsked = [
        ...(input.caseState().askedQuestions || []),
        ...(input.caseState().answeredQuestions || []).map(
          (qa: any) => qa.question,
        ),
      ];
      const dismissed = new Set(
        (input.caseState().dismissedQuestions || []).map((d: any) =>
          (typeof d === "string" ? d : d.question || "").trim().toLowerCase(),
        ),
      );
      const existing = new Set(
        (input.caseState().answeredQuestions || []).map((qa: any) =>
          (qa.question || "").trim().toLowerCase(),
        ),
      );

      const detection = await input.detectQuestions({
        recentTranscript: tagged,
        suggestedQuestions: [],
        alreadyAsked,
        sessionId: input.sessionId() || null,
        contactType: input.contactType(),
        mode: "adhoc",
      });

      const detected = Array.isArray(detection?.detectedQuestions)
        ? detection.detectedQuestions
        : [];
      if (!detected.length) return;

      const toAdd = detected.filter((d) => {
        const key = (d.question || "").trim().toLowerCase();
        if (!key || existing.has(key) || dismissed.has(key)) return false;
        // Accept if the LLM returned a usable answer
        return hasAnswer(d.answer || "");
      });

      if (!toAdd.length) return;

      input.setCaseState((prev) => ({
        ...prev,
        answeredQuestions: [
          ...prev.answeredQuestions,
          ...toAdd.map((d) => ({
            question: d.question,
            answer: d.answer,
            quote: d.quote || "",
            confidence:
              typeof d.confidence === "number" ? d.confidence : undefined,
            source: "posthoc" as any,
            origin: "adhoc" as any,
            method: "backfill" as any,
            answerStatus: "inferred",
            answeredAt: new Date().toISOString(),
          })),
        ],
      }));
      input.persistInterviewMetadata?.();
    } catch {
      // ignore
    }
  }

  async function finalizeSession() {
    try {
      await input.stopAll();
      console.log("useSessionFinalization finalizeSession - stopAll complete");
      
      const canonicalTranscript = input.buildEnglishTranscript(
        input.utterances(),
      );
      console.log("useSessionFinalization finalizeSession - utterances", input.utterances());
      console.log("useSessionFinalization finalizeSession - canonicalTranscript", canonicalTranscript);

      // Upload the transcript as text to the new process-text endpoint

      // 1) Enrichment/backfills
      try {
        input.setPopupMsg("Finalizing session. Reconciling answers");
        await reconcileUnansweredQuestions(canonicalTranscript);
        await detectAdhocQuestions(canonicalTranscript);
      } catch {
        // ignore
      }

      // 2) Insights (best-effort)
      try {
        input.setPopupMsg("Finalizing session. Generating insights");
        await input.fetchInsights(
          canonicalTranscript,
          input.contactType(),
          input.sessionId() || undefined,
        );
      } catch (e) {
        console.error("Failed to generate insights:", e);
        toast.error(
          "Insights generation failed. You can still open the report.",
        );
      }

      // 3) Report draft + mark completed (best-effort)
      const sessionId = input.sessionId();
      if (sessionId) {
        try {
          input.setPopupMsg("Finalizing session. Creating report");
          await input.generateReport(sessionId, {
            contactType: input.contactType() || "Intake",
            forceNewVersion: true,
          });
        } catch {
          // ignore
        }
        try {
          await input.updateSession(sessionId, { status: "completed" });
        } catch (e) {
          console.error("Failed to set session completed:", e);
        }
      }
      try {
        const sid = input.sessionId();
        if (!sid) {
          console.warn("[Intake Ranking] No session ID available, skipping intake ranking");
          return;
        }

        console.log("[Intake Ranking] Step 1: Processing transcript text via processIntakeText");
        var translatedTranscript : string = canonicalTranscript;
        // if source is not english, translate the transcript
        if (!input.sourceLang().startsWith("en")) {
          
          translatedTranscript = buildTranslatedTranscript(
            input.utterances(),
            input.interpreterEnabled(),
          );
        }

        const data = await processIntakeText(translatedTranscript);
        console.log("[Intake Ranking] Step 1 complete. Data received:", { hasNarrative: !!data.narrative, likelihood: data.likelihood });

        console.log("[Intake Ranking] Step 2: Updating session with narrative");
        await input.updateSession(sid, { narrative: data.narrative });
        console.log("[Intake Ranking] Step 2 complete");

        console.log("[Intake Ranking] Step 3: Triggering intake ranking pipeline for session:", sid);
        await triggerIntakeRanking(sid);
        console.log("[Intake Ranking] Step 3 complete - pipeline triggered successfully");

        console.log("[Intake Ranking] Step 4: Fetching updated session data");
        const sessionData = await getSession(sid);
        console.log("[Intake Ranking] Step 4 complete. Session data:", {
          likelihood: sessionData.likelihood,
          screeningDecision: sessionData.screeningDecision,
          maltreatment: sessionData.maltreatment,
          priorityRanking: sessionData.priorityRanking
        });

        if (sessionData) {
          console.log("[Intake Ranking] Step 5: Updating UI with session data");
          input.setLikelihood(sessionData.likelihood);
          input.setScreeningDecision(sessionData.screeningDecision);
          input.setMaltreatment(sessionData.maltreatment);
          input.setPriorityRanking(sessionData.priorityRanking);
          input.setLlmPredictionExplanation(sessionData.llmPredictionExplanation);
          input.setAlerts(sessionData.alarms?.split(",") || []);
          input.setOffenderRole(sessionData.offenderRole);
          input.setVictimType(sessionData.victimType);
          console.log("[Intake Ranking] Step 5 complete - All ranking data populated successfully");
        } else {
          console.warn("[Intake Ranking] No session data returned from getSession");
        }
      } catch (err) {
        console.error("[Intake Ranking] ERROR - Failed to process intake ranking:", err);
        console.error("[Intake Ranking] Error details:", {
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined
        });
      }
    } finally {
      input.setWorkflowStatus("completed");
      input.setReadOnly(true);
      input.setPopupMsg(null);

    }
  }

  return { finalizeSession };
}
